import { useTranslation } from 'react-i18next';

import type { SearchResultOpenData } from '@entities/search-run';
import { VehicleCard } from '@entities/vehicle/VehicleCard';
import { ButtonLink } from '@shared/ui';

import { useFavoriteToggle } from '../model/useFavoriteToggle';
import { vehicleLabel } from '../model/vehicleLabel';

import { FavoriteNotice } from './FavoriteNotice';
import { FavoriteToggle } from './Favorites';

interface ShowcaseCardProps {
  searchId: string;
  runId: string;
  item: SearchResultOpenData;
  /** Адрес карточки автомобиля в заказе — с прогоном, сортировкой и страницей. */
  to: string;
  index?: number | undefined;
}

/**
 * Открытая карточка витрины до оплаты (FE-586, DESIGN_SYSTEM §5.5, макет 03).
 *
 * Фотография с сердечком, марка, модель и модификация, год, пробег, топливо,
 * коробка, город и цена — данные объявления своего рабочего набора бесплатны
 * (BE-580). **Аналитики здесь нет и взять её неоткуда**: Match Score, причины,
 * рынок, признаки внимания и номер места в ответе `open` отсутствуют, а
 * `VehicleCard` их и не принимает.
 *
 * Ссылка одна — «Details ansehen» синим контуром (действие на карточке, §5.1):
 * вторая ссылка на то же место через всю карточку дала бы два одинаковых
 * перехода подряд для клавиатуры и скринридера.
 */
export function ShowcaseCard({ searchId, runId, item, to, index = 0 }: ShowcaseCardProps) {
  const { t } = useTranslation();
  const { toggle, failed, pending } = useFavoriteToggle({
    searchId,
    runId,
    vehicleId: item.vehicle_id,
  });
  const label = vehicleLabel(item);

  return (
    <VehicleCard
      vehicle={item}
      index={index}
      favorite={
        <FavoriteToggle
          pressed={item.is_favorite}
          onToggle={toggle}
          vehicleLabel={label}
          busy={pending}
        />
      }
      notice={failed ? <FavoriteNotice failed={failed} name={label} /> : null}
      action={
        <ButtonLink to={to} variant="outline-action" fullWidth>
          {t('showcase.card.details')}
        </ButtonLink>
      }
    />
  );
}
